/**
 * @fileoverview routes.ts — the svelte-spa-router routes map.
 * @module apps/network/consumer/src/routes
 *
 * Route space:
 *   • account-level — /, /projects (home), /account, /billing
 *   • deployments   — /deployments[/:section]  (the composable, scope-aware
 *                     command center; the scope of projects is held by ScopeIO,
 *                     not the URL path)
 *   • legacy        — /projects/:id[/:section] redirect into /deployments/:section
 *                     with scope set to that one project (back-compat for old
 *                     links + the magic-link returnUrl)
 *
 * Sections (DEPLOYMENT_TABS): overview · releases · code · domain · data ·
 * performance · logs. The DeploymentsSection dispatcher renders the per-project
 * screen (scope=one), the fleet command center (overview, scope≠one), or a
 * project-picker feed (other sections, scope≠one).
 */

import type { Component } from "svelte";

import {
  DEPLOYMENT_TAB_IDS,
  type DeploymentTabId,
} from "./features/deployments/deployment-tabs.js";

import SignInScreen from "./accounts/SignInScreen.svelte";
import ProjectsHome from "./features/ProjectsHome.svelte";
import AccountScreen from "./accounts/AccountScreen.svelte";
import BillingScreen from "./accounts/BillingScreen.svelte";

import DeploymentsSection from "./features/deployments/DeploymentsSection.svelte";
import LegacyProjectRedirect from "./features/deployments/LegacyProjectRedirect.svelte";

/**
 * The routes map svelte-spa-router consumes. Hash-based routing
 * (`/#/deployments/releases`) — no server rewrites needed.
 */
export const ROUTES: Record<string, Component<Record<string, never>>> = {
  // ── Account level ─────────────────────────────────────────────
  "/":             SignInScreen as Component<Record<string, never>>,
  "/projects":     ProjectsHome as Component<Record<string, never>>,
  "/account":      AccountScreen as Component<Record<string, never>>,
  "/billing":      BillingScreen as Component<Record<string, never>>,

  // ── Deployments (scope-aware) ─────────────────────────────────
  "/deployments":          DeploymentsSection as Component<Record<string, never>>,
  "/deployments/:section": DeploymentsSection as Component<Record<string, never>>,

  // ── Legacy single-project links → redirect into /deployments ──
  "/projects/:id":          LegacyProjectRedirect as Component<Record<string, never>>,
  "/projects/:id/:section": LegacyProjectRedirect as Component<Record<string, never>>,

  // Catch-all → SignInScreen (which decides based on identity).
  "*":             SignInScreen as Component<Record<string, never>>,
};

/** The valid section ids (for nav highlighting + path derivation). Derived
 *  from the Deployments feature's tab list — the single source of truth at
 *  `features/deployments/deployment-tabs.ts`, which `DeploymentsHeader` also
 *  renders. One place to add/reorder a section. */
export const PROJECT_SCREENS = DEPLOYMENT_TAB_IDS;

export type ProjectScreenId = DeploymentTabId;
